import { useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { CrmSidebar } from "./CrmSidebar";
import { MobileTopBar } from "./MobileTopBar";
import { AgentPanel, AgentPanelToggle } from "@/components/agent-panel/AgentPanel";

// DEM-45 — voice panel REMOVED from the Engineering Dashboard chrome. The
// agent now produces voice in-thread via a `speak` tool call (see
// agent-runtime.ts), so the standalone Voice Feed rail + its toolbar
// toggle are gone. The PI panel is the only right rail.

const SKY_BLUE_BG = "#F2F7FD";

const SKY_BLUE_ROUTES = [
  "/dashboard-architecture",
  "/tickets",
  "/agents",
  "/skills",
  "/projects",
  "/projects-list",
  "/goals",
  "/goals-list",
  "/kb-doc",
  "/knowledge-base",
];

const FULL_WIDTH_ROUTES = [
  "/tickets",
  "/agents",
];

const NARROW_ROUTES = [
  "/kb-doc",
  "/dashboard-architecture",
];

function matchesRoute(pathname: string, routes: string[]): boolean {
  return routes.some((r) => pathname === r || pathname.startsWith(r + "/"));
}

function backgroundForRoute(pathname: string): string | null {
  if (matchesRoute(pathname, SKY_BLUE_ROUTES)) return SKY_BLUE_BG;
  return null;
}

function isFullWidthRoute(pathname: string): boolean {
  return matchesRoute(pathname, FULL_WIDTH_ROUTES);
}

function isNarrowRoute(pathname: string): boolean {
  return matchesRoute(pathname, NARROW_ROUTES);
}

export const Layout = ({ children }: { children: ReactNode }) => {
  const [agentOpen, setAgentOpen] = useState(true);

  const location = useLocation();
  const customBg = backgroundForRoute(location.pathname);
  const fullWidth = isFullWidthRoute(location.pathname);
  const narrow = isNarrowRoute(location.pathname);

  let contentClass: string;
  if (fullWidth) {
    contentClass = "flex-1";
  } else if (narrow) {
    contentClass = "mx-auto w-full max-w-[1000px] flex-1 px-3 py-8 sm:px-8 sm:py-10 md:px-12";
  } else {
    contentClass = "mx-auto w-full max-w-6xl flex-1 px-3 py-6 sm:px-6 sm:py-8";
  }

  return (
    <div className="flex h-svh">
      <CrmSidebar />
      <main
        className={`relative flex min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto ${customBg ? "" : "bg-background"}`}
        style={customBg ? { background: customBg } : undefined}
      >
        <MobileTopBar />
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30 hidden justify-end px-6 py-4 md:flex">
          <div className="pointer-events-auto flex items-center gap-2">
            {!agentOpen && <AgentPanelToggle onClick={() => setAgentOpen(true)} />}
          </div>
        </div>
        <div className={contentClass}>{children}</div>
      </main>
      <div className="hidden md:contents">
        <AgentPanel open={agentOpen} onClose={() => setAgentOpen(false)} />
      </div>
    </div>
  );
};
